import React from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import classNames from 'classnames';

import './Carousel.scss';
import { colors } from '@styleseat/brand';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

/**
 * A simple carousel which slides through each child element.
 *
 * @example
 *   <Carousel>
 *     <section>
 *       Slide 1
 *     </section>
 *     <section>
 *       <h2>Slide 2</h2>
 *       <p>With formatting</p>
 *     </section>
 *     <section>
 *       Slide 3
 *     </section>
 *   </Carousel>
 */
export default class Carousel extends React.Component {
  static propTypes = {
    /** A class name to apply to the container element */
    className: PropTypes.string,

    /** If the carousel should play automatically,
        set this to the number of milliseconds between slides. */
    autoPlayInterval: PropTypes.number,

    /** If we want to display the slide indicators */
    showIndicators: PropTypes.bool,

    /** If we want to show part of the next and/or previous slides */
    enablePeek: PropTypes.bool,

    /** If we want to show arrows to allow paging between slides */
    enableControls: PropTypes.bool,

    /** One or more <li> elements */
    children: PropTypes.any.isRequired,

    /** Handles change of slide when swiping */
    onChangeSlide: PropTypes.func,

    /** Slide number (zero indexed) to init carousel on */
    initialSlideIndex: PropTypes.number,

    /** Custom color of the pagination key */
    paginationColor: PropTypes.string,

    /** Disables touch events (when you don't want users to swipe) */
    disabled: PropTypes.bool,

    /** Name of the carousel */
    name: PropTypes.string,
  };

  static defaultProps = {
    className: '',
    autoPlayInterval: 3000,
    showIndicators: true,
    enablePeek: false,
    enableControls: false,
    initialSlideIndex: 0,
    onChangeSlide: () => { },
    paginationColor: '',
    disabled: false,
    name: '',
  };

  /**
   * sets the number of slides for the Carousel and stores the actual slides.
   * We also filter out slides that are null or undefined so we do not render
   * a blank slide and have an accurate count of actual slides.
   * @param {object} props - props object passed into carousel component
   * @returns {object} - object of total slide number and the slides
   */
  static getDerivedStateFromProps(props) {
    const {
      children,
      initialSlideIndex,
    } = props;
    // filter out blank slides
    const slides = React.Children.map(children, slide => {
      if (slide) {
        return slide;
      }
      return null;
    });
    return {
      slides,
      numSlides: slides.length,
      currentSlideIndex: initialSlideIndex,
    };
  }

  state = {
    numSlides: 0,
    slides: [],
    autoScroll: !!this.props.autoPlayInterval,
    currentSlideIndex: this.props.initialSlideIndex || 0,
  };

  /**
   * Called when the carousel slide index changes
   * If some sort of tracking is passed down into the carousel,
   * we can track within this function
   *
   * @param {number} index - The current slide index
   */
  handleChangeIndex = index => {
    const { slides } = this.state;
    this.props.onChangeSlide(slides[index], index);
    this.setState({
      currentSlideIndex: index,
    });
  };

  /**
   * Stops carousel from auto scrolling
   */
  handleSwitching = () => {
    this.setState({
      autoScroll: false,
    });
  };

  /**
   * Handle when the user clicks a pagination dot
   */
  handlePaginationClick = evt => {
    const { slides } = this.state;
    const dot = evt.target;
    const index = Number(dot.dataset.index);
    // eslint-disable-next-line no-restricted-globals
    if (typeof index === 'number') {
      this.props.onChangeSlide(slides[index], index);
      this.setState({
        currentSlideIndex: index,
      });
    }
  };

  /**
   * Returns the element to display the pagination indicator
   *
   * @param {array} paginationIndicators - array of the total number of slides
   * @param {number} index - index of the current slide that is being viewed
   * @return {Element}
   */
  buildPaginationIndicators = (paginationIndicators, index) => {
    const {
      paginationColor,
      name,
    } = this.props;

    return (
      <ol data-testid={`${name}-pagination-container`}>
        {paginationIndicators.map((s, i) => (
          <li key={`pagination-key-${s + i}`}>
            <span
              role="presentation"
              className={(i === index) ? 'active' : ''}
              data-index={i}
              onClick={this.handlePaginationClick}
              data-testid={`pagination-slide-index-${i}`}
              style={{
                backgroundColor: (i === index) ? (paginationColor || colors.slate) : colors.silver,
              }}
            />
          </li>
        ))}
      </ol>
    );
  };

  render() {
    const {
      className,
      autoPlayInterval,
      showIndicators,
      enablePeek,
      enableControls,
      disabled,
      name,
    } = this.props;

    const {
      autoScroll,
      numSlides,
      currentSlideIndex,
    } = this.state;

    const paginationIndicators = (new Array(numSlides)).fill(1);
    const SwipeableComponent = (autoPlayInterval)
      ? AutoPlaySwipeableViews
      : SwipeableViews;

    return (
      <section
        className={classNames(
          'ss-carousel',
          className,
          {
            'enable-peek': enablePeek,
            'first-slide': currentSlideIndex === 0,
            'last-slide': currentSlideIndex === numSlides - 1,
          },
        )}
        data-testid={name}
      >
        <SwipeableComponent
          enableMouseEvents
          index={currentSlideIndex}
          interval={autoPlayInterval}
          autoPlay={autoScroll}
          disabled={disabled}
          onChangeIndex={this.handleChangeIndex}
          onSwitching={this.handleSwitching}
        >
          {React.Children.toArray(this.props.children).filter(Boolean)}
        </SwipeableComponent>
        <nav className="ss-carousel-pagination">
          {showIndicators && (
            this.buildPaginationIndicators(paginationIndicators, currentSlideIndex)
          )}
        </nav>
        {enableControls && (
          <div className="carousel-controls">
            <span
              role="presentation"
              className="carousel-control carousel-control-prev"
              onClick={() => this.handleChangeIndex(currentSlideIndex - 1)}
            />
            <span
              role="presentation"
              className="carousel-control carousel-control-next"
              onClick={() => this.handleChangeIndex(currentSlideIndex + 1)}
            />
          </div>
        )}
      </section>
    );
  }
}
